import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useCallback,
} from "react";

import { Container } from "@pixi/react";

/**
 * PotCollectionCollectionLayer
 *
 * High-zIndex container that hosts value-text travel animations
 * (coin values flying to Collect Zeus, trail effects, etc.).
 * Sits above all other Pot Collection layers.
 *
 * Story 9.4 will add PotCollectionValueTraveler here.
 * For now: shell with cancel/cleanup API.
 */
const PotCollectionCollectionLayer = forwardRef(
  ({ symbolWidth, symbolHeight }, ref) => {
    const containerRef = useRef(null);
    const activeAnimationsRef = useRef([]);

    // Update dimensions on resize (future animator will use these)
    const dimensionsRef = useRef({ symbolWidth, symbolHeight });
    useEffect(() => {
      dimensionsRef.current = { symbolWidth, symbolHeight };
    }, [symbolWidth, symbolHeight]);

    const cancel = useCallback(() => {
      // Cancel all active travel animations
      for (const anim of activeAnimationsRef.current) {
        if (anim?.cancel) anim.cancel();
      }
      activeAnimationsRef.current = [];

      // Remove all children from container
      if (containerRef.current) {
        while (containerRef.current.children.length > 0) {
          containerRef.current.removeChildAt(0);
        }
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getContainer: () => containerRef.current,
        cancel,
      }),
      [cancel],
    );

    // No Y offset — _getGridPosition already computes full coordinates
    // relative to the parent container's origin (reelset center).
    return (
      <Container
        ref={containerRef}
        name="pot_collection_collection_layer"
        zIndex={25}
        sortableChildren
      />
    );
  },
);

PotCollectionCollectionLayer.displayName = "PotCollectionCollectionLayer";

export default PotCollectionCollectionLayer;
